// Live updates: one WebSocket per open organisation (GET /orgs/{orgId}/live). The overview arrives on
// connect, then each asset whose status changed; both are merged into the cached overview, so every
// view reading it updates. Alarm lists are refreshed when an asset's alarm state moves.
//
// Probe values of a device in live mode (docs/device-protocol.md §16) go to the views that subscribed with
// onLiveProbe() (the device page). The socket tells the hub when the tab is hidden: devices open their watch
// windows only while a dashboard is visible.

import type { AssetStatus, LiveProbeValue, Overview } from '@cck/schema';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { API_BASE } from './api.ts';
import { keys } from './queries.ts';

export type LiveState = 'connecting' | 'live' | 'offline';

type Message = { type: 'snapshot'; data: Overview } | { type: 'asset'; data: AssetStatus }
  | { type: 'probe'; data: LiveProbeValue };

const probeListeners = new Map<string, Set<(v: LiveProbeValue) => void>>();

/** Calls `fn` with each live probe value of the organisation; returns the unsubscribe function. */
export function onLiveProbe(orgId: string, fn: (v: LiveProbeValue) => void): () => void {
  let set = probeListeners.get(orgId);
  if (!set) probeListeners.set(orgId, (set = new Set()));
  set.add(fn);
  return () => {
    set.delete(fn);
  };
}

const presence = () => JSON.stringify({ type: 'presence', visible: document.visibilityState !== 'hidden' });

/** Merges a live asset update into an overview (positions come from the HTTP overview only). */
export function mergeAsset(overview: Overview | undefined, a: AssetStatus): Overview | undefined {
  if (!overview) return overview;
  const i = overview.assets.findIndex((x) => x.asset_id === a.asset_id);
  if (i < 0) return overview;  // an asset this view does not show (another site, or archived)
  const assets = overview.assets.slice();
  assets[i] = { ...a, position: assets[i]!.position };
  return { ...overview, assets, generated_at: new Date().toISOString() };
}

/** Merges a live snapshot: assets the hub has no data for keep their database view. */
export function mergeSnapshot(overview: Overview | undefined, live: Overview): Overview | undefined {
  if (!overview) return overview;
  const byId = new Map(live.assets.map((a) => [a.asset_id, a]));
  return { ...overview, source: 'live', generated_at: live.generated_at,
    assets: overview.assets.map((a) => (byId.has(a.asset_id) ? { ...byId.get(a.asset_id)!, position: a.position } : a)) };
}

/** `enabled` false (a kiosk, which polls instead): no connection, and the state stays offline. */
export function useLive(orgId: string, enabled = true): LiveState {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [state, setState] = useState<LiveState>('connecting');

  useEffect(() => {
    if (!enabled) {
      setState('offline');
      return;
    }
    let ws: WebSocket | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let ping: ReturnType<typeof setInterval> | undefined;
    let attempt = 0;
    let stopped = false;
    const key = keys.scoped(orgId, 'overview');

    const connect = () => {
      const url = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}${API_BASE}/orgs/${orgId}/live`;
      ws = new WebSocket(url);
      setState('connecting');
      ws.onopen = () => {
        attempt = 0;
        setState('live');
        if (document.visibilityState === 'hidden') ws?.send(presence());
        ping = setInterval(() => ws?.readyState === WebSocket.OPEN && ws.send('ping'), 30_000);
      };
      ws.onmessage = (e) => {
        if (typeof e.data !== 'string' || e.data === 'pong') return;
        const m = JSON.parse(e.data) as Message;
        if (m.type === 'probe') {
          for (const fn of probeListeners.get(orgId) ?? []) fn(m.data);
        } else if (m.type === 'snapshot') {
          qc.setQueryData<Overview>(key, (o) => mergeSnapshot(o, m.data));
        } else if (m.type === 'asset') {
          const before = qc.getQueryData<Overview>(key)?.assets.find((a) => a.asset_id === m.data.asset_id);
          qc.setQueryData<Overview>(key, (o) => mergeAsset(o, m.data));
          qc.invalidateQueries({ queryKey: keys.scoped(orgId, 'asset', m.data.asset_id) });
          if (!before || before.status !== m.data.status || before.awaiting_acknowledgement !== m.data.awaiting_acknowledgement) {
            qc.invalidateQueries({ queryKey: keys.scoped(orgId, 'alarms') });
          }
        }
      };
      ws.onclose = (e) => {
        clearInterval(ping);
        if (stopped) return;
        setState('offline');
        if (e.code === 4401) {
          // The session ended (signed out elsewhere, or revoked).
          qc.setQueryData(keys.me, null);
          navigate('/auth/sign-in');
          return;
        }
        if (e.code === 4403) {
          navigate('/');
          return;
        }
        const delay = Math.min(30_000, 1000 * 2 ** attempt++) * (0.5 + Math.random() / 2);
        timer = setTimeout(connect, delay);
      };
    };
    const visibility = () => {
      if (ws?.readyState === WebSocket.OPEN) ws.send(presence());
    };
    document.addEventListener('visibilitychange', visibility);
    connect();
    return () => {
      stopped = true;
      document.removeEventListener('visibilitychange', visibility);
      clearTimeout(timer);
      clearInterval(ping);
      ws?.close(1000, 'leaving');
    };
  }, [orgId, qc, navigate, enabled]);

  return state;
}
